import deepEqual from 'deep-equal';
import { DependencyList, useCallback, useRef, useState } from 'react';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';

/**
 * 二度押し防止し処理中にローディングを行う関数を作成する
 */
const useOnceSubmit = <T>(
  param: {
    onPreviousSubmit?: (args: T) => Promise<boolean>;
    onSubmit: (args: T) => Promise<void>;
  },
  deps: DependencyList,
): ((args: T) => Promise<void>) => {
  const submittingRef = useRef(false);
  const previousValueRef = useRef<T | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const { showLoading, hideLoading } = useGlobalLoadingHandler();

  const callback = useCallback(
    async (args: T) => {
      try {
        if (!deepEqual(args, previousValueRef.current)) {
          submittingRef.current = false;
        }

        if (submittingRef.current) return;
        previousValueRef.current = args;
        submittingRef.current = true;

        if (param.onPreviousSubmit) {
          if (!(await param.onPreviousSubmit(args))) {
            return;
          }
        }

        showLoading();

        await param.onSubmit(args);
      } finally {
        // 少し経過してからfalseにする 連打防止
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => {
          submittingRef.current = false;
          hideLoading();
        }, 500);
      }
    },
    [...deps, param.onPreviousSubmit, param.onSubmit, showLoading, hideLoading],
  );

  return callback;
};

export default useOnceSubmit;
